import { useRef, useCallback, useEffect, useState } from 'react';

/**
 * Custom hook for intelligent auto-scrolling in streaming chat
 * Follows best practices for chat UX and user intent detection
 */
export const useAutoScroll = () => {
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const containerRef = useRef(null);
  const endRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const lastScrollTop = useRef(0);
  const isScrollingProgrammatically = useRef(false);
  const streamingScrollThrottle = useRef(null);
  const userScrollIntentTimer = useRef(null);
  const resizeObserverRef = useRef(null);
  
  // Thresholds for auto-scroll behavior - MORE USER-FRIENDLY
  const SCROLL_THRESHOLD = 200; // px from bottom to consider "at bottom" (increased)
  const DEBOUNCE_MS = 150; // Debounce scroll events (increased)
  const USER_SCROLL_THRESHOLD = 100; // px scroll distance to detect user intent (increased)
  // const STREAMING_THROTTLE_MS = 300; // Throttle streaming auto-scrolls (future feature)
  const USER_INTENT_TIMEOUT = 2000; // ms to remember user scroll intent (NEW)
  
  /**
   * Check if user is near the bottom of the chat
   */
  const isNearBottom = useCallback(() => {
    if (!containerRef.current) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    return distanceFromBottom <= SCROLL_THRESHOLD;
  }, []);
  
  /**
   * Smoothly scroll to bottom
   */
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    // Handle instant scrolling differently
    if (behavior === 'instant') {
      // Use direct scroll without animation
      if (containerRef.current) {
        const container = containerRef.current;
        const maxScroll = container.scrollHeight - container.clientHeight;
        if (typeof container.scrollTo === 'function') {
          container.scrollTo({ top: maxScroll, behavior: 'auto' });
        } else {
          container.scrollTop = maxScroll;
        }
      }
      return;
    }
    
    isScrollingProgrammatically.current = true;
    
    // Prefer scrollIntoView if sentinel exists; otherwise, scroll container
    if (endRef.current) {
      endRef.current.scrollIntoView({ 
        behavior, 
        block: 'end',
        inline: 'nearest'
      });
    } else if (containerRef.current) {
      const container = containerRef.current;
      const maxScroll = container.scrollHeight - container.clientHeight;
      if (typeof container.scrollTo === 'function') {
        container.scrollTo({ top: maxScroll, behavior });
      } else {
        container.scrollTop = maxScroll;
      }
    }
    
    // Reset programmatic flag after scroll completes
    setTimeout(() => {
      isScrollingProgrammatically.current = false;
    }, behavior === 'smooth' ? 600 : 100);
  }, []);
  
  /**
   * Force scroll to bottom (for when user clicks "scroll to bottom" button)
   */
  const forceScrollToBottom = useCallback(() => {
    // console.log('User clicked force scroll to bottom');
    setIsUserScrolledUp(false);
    setShowScrollToBottom(false);
    scrollToBottom('smooth');
  }, [scrollToBottom]);
  
  /**
   * Handle scroll events with IMPROVED user intent detection
   */
  const handleScroll = useCallback(() => {
    if (isScrollingProgrammatically.current) {
      return; // Ignore programmatic scrolls
    }
    
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      if (!containerRef.current) return;
      
      const { scrollTop } = containerRef.current;
      const scrollDelta = scrollTop - lastScrollTop.current;
      lastScrollTop.current = scrollTop;
      
      const nearBottom = isNearBottom();
      
      // console.log('Scroll detected:', {
      //   scrollDelta,
      //   nearBottom,
      //   currentScrollTop: scrollTop,
      //   userScrolledUp: isUserScrolledUp
      // });
      
      // IMPROVED: Stronger user intent detection
      // If user scrolled up significantly, they CLEARLY want to stay where they are
      if (scrollDelta < -USER_SCROLL_THRESHOLD) {
        // console.log('User scrolled up significantly - disabling auto-scroll');
        setIsUserScrolledUp(true);
        setShowScrollToBottom(true);
        
        // Remember user intent for a while, even during streaming
        if (userScrollIntentTimer.current) {
          clearTimeout(userScrollIntentTimer.current);
        }
        userScrollIntentTimer.current = setTimeout(() => {
          // console.log('User scroll intent timeout - checking if still away from bottom');
          if (!isNearBottom()) {
              // console.log('User still away from bottom, maintaining scroll state');
          } else {
            // console.log('User back near bottom, re-enabling auto-scroll');
            setIsUserScrolledUp(false);
            setShowScrollToBottom(false);
          }
        }, USER_INTENT_TIMEOUT);
      }
      // If user scrolled to near bottom, resume auto-scroll
      else if (nearBottom && isUserScrolledUp) {
        // console.log('User scrolled back to bottom - re-enabling auto-scroll');
        setIsUserScrolledUp(false);
        setShowScrollToBottom(false);
        
        if (userScrollIntentTimer.current) {
          clearTimeout(userScrollIntentTimer.current);
        }
      }
      
    }, DEBOUNCE_MS);
  }, [isNearBottom, isUserScrolledUp]);
  
  /**
   * Instant scroll for initial load (no animation) - ROBUST VERSION
   */
  const autoScrollOnInitialLoad = useCallback(() => {
    if (containerRef.current) {
      
      const scrollToBottomInstant = () => {
        const container = containerRef.current;
        if (!container) return false;
        
        // Force layout calculation multiple times to ensure all content is rendered
        void container.offsetHeight;
        void container.scrollHeight;
        void container.clientHeight;
        
        const maxScroll = container.scrollHeight - container.clientHeight;
        if (typeof container.scrollTo === 'function') {
          container.scrollTo({ top: maxScroll, behavior: 'auto' });
        } else {
          container.scrollTop = maxScroll;
        }
        
        // Verify we're actually at the bottom with more tolerance
        const currentScroll = container.scrollTop;
        const isAtBottom = Math.abs(currentScroll - maxScroll) < 10; // Increased tolerance
        
        // console.log('Scroll attempt:', {
        //   scrollHeight: container.scrollHeight,
        //   clientHeight: container.clientHeight,
        //   maxScroll,
        //   currentScroll,
        //   isAtBottom,
        //   difference: Math.abs(currentScroll - maxScroll)
        // });
        
        return isAtBottom;
      };
      
      // Try immediately
      if (!scrollToBottomInstant()) {
        // console.log('First attempt failed, trying with requestAnimationFrame');
        // If first attempt failed, try again after layout
        requestAnimationFrame(() => {
          if (!scrollToBottomInstant()) {
            // console.log('Second attempt failed, trying with 100ms delay');
            // Final attempt with a delay for any async content
            setTimeout(() => {
              if (!scrollToBottomInstant()) {
                // console.log('Third attempt failed, trying with 300ms delay');
                // Extra attempt for complex content like citations
                setTimeout(() => {
                  if (!scrollToBottomInstant()) {
                    // console.log('Fourth attempt failed, trying with 500ms delay for images');
                    // Final attempt for images and heavy content
                    setTimeout(() => {
                      scrollToBottomInstant();
                    }, 500);
                  }
                }, 300);
              }
            }, 100);
          }
        });
      }
    }
  }, []);
  
  /**
   * Auto-scroll on new content (if user hasn't scrolled up)
   */
  const autoScrollOnNewContent = useCallback((behavior = 'smooth') => {
    if (!isUserScrolledUp && containerRef.current && endRef.current) {
      // console.log('Auto-scrolling for new content with behavior:', behavior);
      
      if (behavior === 'instant') {
        // Use the robust instant scroll for initial loads
        autoScrollOnInitialLoad();
      } else {
        // Use smooth scrolling for new messages
        requestAnimationFrame(() => {
          scrollToBottom(behavior);
        });
      }
    } else {
      // console.log('Skipping auto-scroll for new content (user scrolled up)');
    }
  }, [isUserScrolledUp, scrollToBottom, autoScrollOnInitialLoad]);
  
  /**
   * IMPROVED: Robust auto-scroll for streaming content and layout changes
   * Handles collapses, content updates, and maintains bottom position
   * WITH BUILT-IN THROTTLING to prevent excessive calls
   * NOW WITH ANIMATION AWARENESS to prevent interference
   */
  const autoScrollOnStreaming = useCallback(() => {
    // BLOCK DURING COLLAPSES: Check global flag
    if (window._blockAutoScroll) {
      console.log('Auto-scroll blocked due to active collapse');
      return;
    }
    
    // RESPECT USER INTENT: If user scrolled up, don't auto-scroll AT ALL
    if (isUserScrolledUp) {
      return;
    }
    
    // THROTTLE: Only auto-scroll if we haven't recently done so
    if (streamingScrollThrottle.current) {
      return; // Skip this call, we're throttling
    }
    
    if (containerRef.current) {
      const container = containerRef.current;
      
      // Check if we're still near bottom before scrolling
      if (isNearBottom()) {
        // Check if any collapse animations are running before auto-scrolling
        const animatingElements = container.querySelectorAll('[style*="transition"]');
        const hasCollapseAnimations = Array.from(animatingElements).some(el => 
          el.style.transition.includes('height') && 
          el.style.height === '0px'
        );
        
        if (hasCollapseAnimations) {
          // console.log('Skipping auto-scroll due to collapse animation');
          return;
        }
        
        // For layout changes (like collapses), be more aggressive
        // Use multiple attempts to handle DOM changes
        const maxScroll = container.scrollHeight - container.clientHeight;
        if (typeof container.scrollTo === 'function') {
          container.scrollTo({ top: maxScroll, behavior: 'auto' });
        } else {
          container.scrollTop = maxScroll;
        }
        
        // Also try after next frame to handle delayed layout changes
        requestAnimationFrame(() => {
          if (container && !isUserScrolledUp && isNearBottom()) {
            const maxScrollNext = container.scrollHeight - container.clientHeight;
            if (typeof container.scrollTo === 'function') {
              container.scrollTo({ top: maxScrollNext, behavior: 'auto' });
            } else {
              container.scrollTop = maxScrollNext;
            }
          }
        });
        
        // console.log('Auto-scroll executed for content/layout change');
        
        // Set throttle to prevent rapid successive calls
        streamingScrollThrottle.current = setTimeout(() => {
          streamingScrollThrottle.current = null;
        }, 100); // Allow max 10 calls per second
      }
    }
  }, [isUserScrolledUp, isNearBottom]);
  
  /**
   * IMPROVED: Auto-scroll on streaming completion with better reliability
   */
  const autoScrollOnStreamingComplete = useCallback(() => {
    // console.log('autoScrollOnStreamingComplete called', { isUserScrolledUp });
    
    // BLOCK DURING COLLAPSES: Check global flag
    if (window._blockAutoScroll) {
      console.log('Auto-scroll completion blocked due to active collapse');
      return;
    }
    
    // Clear any throttling
    if (streamingScrollThrottle.current) {
      clearTimeout(streamingScrollThrottle.current);
      streamingScrollThrottle.current = null;
    }
    
    if (!isUserScrolledUp && containerRef.current) {
      // Use multiple strategies to ensure final scroll works
      const scrollToBottomFinal = () => {
        if (containerRef.current && !isUserScrolledUp) {
          const container = containerRef.current;
          
          // Try both methods for reliability
          const maxScroll = container.scrollHeight - container.clientHeight;
          if (typeof container.scrollTo === 'function') {
            container.scrollTo({ top: maxScroll, behavior: 'auto' });
          } else {
            container.scrollTop = maxScroll;
          }
          
          // Also use scrollIntoView as backup
          if (endRef.current) {
            endRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }
          
          // console.log('Final completion scroll executed');
        }
      };
      
      // Try immediately
      scrollToBottomFinal();
      
      // Try again after a short delay to handle any layout changes
      setTimeout(scrollToBottomFinal, 100);
      
      // Final attempt after longer delay
      setTimeout(scrollToBottomFinal, 300);
    } else {
      // Intentionally silent when user has scrolled up
    }
  }, [isUserScrolledUp]);
  
  /**
   * NEW: Handle layout changes with scroll preservation
   * This is specifically for collapse/expand animations
   */
  const handleLayoutChange = useCallback((anchorElement, callback) => {
    if (!containerRef.current || !anchorElement) {
      callback?.();
      return;
    }

    // Get anchor position before layout change
    const anchorTopBefore = anchorElement.getBoundingClientRect().top;
    const scrollTopBefore = containerRef.current.scrollTop;

    // Execute the layout change
    callback?.();

    // Compensate scroll position after layout change
    requestAnimationFrame(() => {
      if (containerRef.current && anchorElement) {
        const anchorTopAfter = anchorElement.getBoundingClientRect().top;
        const delta = anchorTopAfter - anchorTopBefore;
        
        // Only adjust if there's a significant change and user hasn't scrolled
        if (Math.abs(delta) > 5 && !isUserScrolledUp) {
          const container = containerRef.current;
          container.scrollTop = scrollTopBefore + delta;
          
          // console.log('Layout change scroll compensation:', { delta, oldScrollTop: scrollTopBefore, newScrollTop: container.scrollTop });
        }
      }
    });
  }, [isUserScrolledUp]);
  
  /**
   * Reset scroll state (for new conversations)
   */
  const resetScrollState = useCallback(() => {
    // console.log('Resetting scroll state for new conversation');
    setIsUserScrolledUp(false);
    setShowScrollToBottom(false);
    lastScrollTop.current = 0;
    
    // Clear any timers
    if (userScrollIntentTimer.current) {
      clearTimeout(userScrollIntentTimer.current);
    }
    if (streamingScrollThrottle.current) {
      clearTimeout(streamingScrollThrottle.current);
      streamingScrollThrottle.current = null;
    }
    
    // Scroll to bottom immediately for new conversations using instant scroll
    setTimeout(() => {
      autoScrollOnInitialLoad();
    }, 50); // Reduced from 100ms for faster loading
  }, [autoScrollOnInitialLoad]);
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (userScrollIntentTimer.current) {
        clearTimeout(userScrollIntentTimer.current);
      }
      if (streamingScrollThrottle.current) {
        clearTimeout(streamingScrollThrottle.current);
      }
      if (resizeObserverRef.current) {
        try { resizeObserverRef.current.disconnect(); } catch (_) {}
        resizeObserverRef.current = null;
      }
    };
  }, []);
  
  // Observe container size changes (images loading, layout shifts) and keep at bottom if appropriate
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let rafId = null;
    const ro = new ResizeObserver(() => {
      if (!containerRef.current) return;
      if (isUserScrolledUp) return;
      if (!isNearBottom()) return;
      
      // FIX: Only auto-scroll during active streaming or recent content changes
      // Check if there's active streaming by looking for streamingScrollThrottle
      if (streamingScrollThrottle.current) {
        // Active streaming - allow auto-scroll
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const c = containerRef.current;
          if (!c) return;
          const maxScroll = c.scrollHeight - c.clientHeight;
          if (typeof c.scrollTo === 'function') {
            c.scrollTo({ top: maxScroll, behavior: 'auto' });
          } else {
            c.scrollTop = maxScroll;
          }
        });
      }
      // If not streaming, don't auto-scroll - this prevents unwanted scrolling
      // when user is near bottom but no new content is being added
    });
    try {
      ro.observe(container);
      resizeObserverRef.current = ro;
    } catch (_) {}
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      try { ro.disconnect(); } catch (_) {}
      if (resizeObserverRef.current === ro) {
        resizeObserverRef.current = null;
      }
    };
  }, [isUserScrolledUp, isNearBottom]);

  // Handle mobile virtual keyboard and viewport changes (iOS/Android) – keep bottom if user was near bottom
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const onViewportChange = () => {
      if (isUserScrolledUp) return;
      if (!isNearBottom()) return;
      
      // FIX: Only auto-scroll during active streaming or recent content changes
      // Check if there's active streaming by looking for streamingScrollThrottle
      if (streamingScrollThrottle.current) {
        // Active streaming - allow auto-scroll
        setTimeout(() => {
          scrollToBottom('instant');
        }, 0);
      }
      // If not streaming, don't auto-scroll - this prevents unwanted scrolling
      // when user is near bottom but no new content is being added
    };
    try {
      vv.addEventListener('resize', onViewportChange);
      vv.addEventListener('scroll', onViewportChange);
    } catch (_) {}
    return () => {
      try {
        vv.removeEventListener('resize', onViewportChange);
        vv.removeEventListener('scroll', onViewportChange);
      } catch (_) {}
    };
  }, [isUserScrolledUp, isNearBottom, scrollToBottom]);

  return {
    // Refs to attach to DOM elements
    containerRef,     // Attach to scrollable container
    endRef,          // Attach to element at bottom of chat
    
    // Scroll functions
    scrollToBottom,
    forceScrollToBottom,
    autoScrollOnNewContent,
    autoScrollOnInitialLoad,
    autoScrollOnStreaming,
    autoScrollOnStreamingComplete,
    resetScrollState,
    handleLayoutChange, // NEW: For handling layout changes with scroll preservation
    
    // Event handlers
    handleScroll,
    
    // State for UI components
    isUserScrolledUp,
    showScrollToBottom,
    isNearBottom,
  };
};

export default useAutoScroll; 